import { ChangeDetectionStrategy, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit,
  Output } from '@angular/core';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { filter, map, take, } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { IclinicalTrials } from '@ispm/therapies/interfaces/clinical-trials.interface';
import { get, isNil } from 'lodash';
import {
  ISelectionResource,
  ISortConfig,
  ISubjectSelectionSection
} from '@ispm/therapies/interfaces/view-configuration.interface';
import { ConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { ResolvableComponent } from '@ispm/core/decorators/resolvable-component.decorator';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import * as jmespath from 'jmespath';
import { FormGroup } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { DiagnosticOrderActionsService } from '@ispm/dashboard/test/services/diagnostic-order-actions.service';
import { TherapiesService } from '@ispm/therapies/services/therapies.service';
import {
  DEFAULT_CLINICALRECOMMENDATION_GROUP_SORT_CONFIG, RESOURCE_TYPE_NAME
} from '@ispm/therapies/constants/default-clinical-recommendation-sorting-configuration.constant';
import { ISubjectIdentifier } from "@ispm/therapies/interfaces/subject.interface";
import { IDefinition } from "../../../configuration/interfaces/definition.interface";
import { ConfigurationStoreHandler } from "../../../configuration/services/configuration-store-handler.service";
import { NavigationConfigService } from "@ispm/navigation/services/navigationConfig.service";

@Component({
  selector: 'gen-treatment-subject',
  templateUrl: './treatment-subject.component.html',
  styleUrls: ['./treatment-subject.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@ResolvableComponent(null, 'TreatmentSubject')
export class TreatmentSubjectComponent implements OnInit, OnDestroy, IWidgetItem {
  @Input()
  set subjectsListConfiguration(configuration: any) {
    this._subjectsListConfiguration = configuration;
    this.initSubjects();
    this.initEditVisible();
    this.resetShowInReportToggle();
    this.resetShowInMIToggle();

    if (!get(this.subjectsListConfiguration, 'widgetPayload.autoSelectSubject', true)) {
      this.autoSelectSubject = this.subjectsListConfiguration.widgetPayload.autoSelectSubject;
    }
  }

  get subjectsListConfiguration() {
    return this._subjectsListConfiguration;
  }

  public get subjectId() {
    return this._subjectId;
  }

  public set subjectId(subjectId: string) {
    this._subjectId = subjectId;
  }

  public get subjectIdPath() {
    return this._subjectIdPath;
  }

  public set subjectIdPath(subjectIdPath: string) {
    this._subjectIdPath = subjectIdPath;
  }

  public get subjectSourceType() {
    return this._subjectSourceType;
  }

  public set subjectSourceType(subjectSourceType: string) {
    this._subjectSourceType = subjectSourceType;
  }

  public get MIToMDA() {
    return this._MIToMDA;
  }

  public set MIToMDA(MIToMDA: boolean) {
    this._MIToMDA = MIToMDA;
    this.onchangeOpinionOfMI.emit(this._MIToMDA);
  }

  private _MIToMDA: boolean;

  constructor(
    private therapiesAC: TherapiesActionCreatorService,
    private diagnosticOrderActionsServiceAC: DiagnosticOrderActionsService,
    private therapiesService: TherapiesService,
    private _ngZone: NgZone,
    private configurationHandler: ConfigurationStoreHandler,
    private navigationConfigService: NavigationConfigService
  ) {
  }

  set widgetConfig(conf: IdynamicFormItem<ISubjectSelectionSection>) {
    this.subjectsListConfiguration = conf.widgetPayload;
  }

  @Input()
  public set deselectSubject(select) {
    if (select) {
      this._ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          select = false;
        }, 0);
      });
    }
  }

  public set widgetData(data: any) {
    if (data && this.subjectsListConfiguration.explicitWidget) {
      this._widgetData = data;

      const therapiesGroups = this.prepareListData(this._widgetData, this.subjectsListConfiguration.resources[0]);
      this.aberrationGroupsByResource$ = of([{
        therapiesGroups: therapiesGroups,
        selectionResourceConfig: this.subjectsListConfiguration.resources[0]
      }]);
    }
  }

  public get widgetData() {
    return this._widgetData;
  }

  @Output()
  public onSubjectIdentifierChange: EventEmitter<ISubjectIdentifier> = new EventEmitter<ISubjectIdentifier>();

  @Output()
  public onchangeOpinionOfMI: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  public reflexMessage: string;

  @Input()
  public isEditable: boolean;

  @Input()
  public showInReportToggleActive: boolean;

  @Input()
  public showInMIToggleActive: boolean;

  @Input()
  public reportEditable: boolean;

  @Input()
  public autoSelectSubject: boolean = true;

  public widgetFormInstance: FormGroup;

  public aberrationGroupsByResource$: Observable<{ therapiesGroups: any[], selectionResourceConfig: ISelectionResource}[]>;

  public _subjectSourceType: string;

  public _destroyer: Subject<any> = new Subject();

  public editVisible = true;

  // it's not undefined. What is setting up the formId?
  public formId: string;

  public widgetUpdateValueCallback: (value) => void;

  public forceUpdate: (value) => void;

  public molecularFindingToMI: any[];

  private _subjectsListConfiguration: any;

  private _subjectId: string;

  private _subjectIdPath: string;

  private _widgetData: any;

  public trials$: Observable<IclinicalTrials[]>;

  public testConfig$: Observable<IDefinition>;

  private initEditVisible() {
    this.editVisible = get(this.subjectsListConfiguration, 'editTreatmentsEnabled', true);
    const editingRules = get(this.subjectsListConfiguration, 'editTreatmentsForDisciplineEnabled');

    if (editingRules) {
      this.therapiesAC.getPermittedActions()
        .pipe(
          filter(actions => !!actions),
          take(1)
        )
        .subscribe(actions => this.isEditable = actions.edit);

      this.diagnosticOrderActionsServiceAC.getEditAllowedForDiscipline(editingRules)
        .pipe(take(1))
        .subscribe(allowed => this.editVisible = allowed);
    }
  }

  private initSubjects() {
    if (!this.subjectsListConfiguration.explicitWidget) {
      this.aberrationGroupsByResource$ = this.therapiesAC.prefetchTestResources().pipe(
        map(resourcesMap =>
          (this.subjectsListConfiguration as ISubjectSelectionSection).resources.map(selectionResourceConfig => {
            // trials array merging with relevant resources array for the new genomics tests compatibility
            const appId = this.navigationConfigService.getCurrentApplicationId();
            const trials = (appId !== 'ISPM-ONCOLOGY-PATHWAYS-NAVIGATOR-APP') && resourcesMap[RESOURCE_TYPE_NAME.TRIALS] || [];
            const therapiesGroups = this.prepareListData([...resourcesMap[selectionResourceConfig.name]], selectionResourceConfig);
            const trialsGroups = this.prepareListData([...trials], selectionResourceConfig);
            this.molecularFindingToMI = this.molecularFinding([...resourcesMap[selectionResourceConfig.name]],selectionResourceConfig);

            trialsGroups.forEach((trial) => {
              therapiesGroups.forEach((therapy, idx) => {
                if (therapy.key !== undefined && trial.key !== undefined && therapy.key === trial.key) {
                  therapiesGroups[idx].subjects = therapy.subjects.concat(trial.subjects);
                }
              })
            });

            return {
              therapiesGroups: therapiesGroups,
              selectionResourceConfig
            };
          })
        ),
      );
    }
  }

  ngOnInit() {
    this.trials$ = this.therapiesAC.getTrials();
    this.testConfig$ = this.configurationHandler.getCurrentConfig();
  }

  ngOnDestroy(): void {
    this._destroyer.next();
    this._destroyer.complete();
  }

  public changeOpinionOfMI(MIToMDA: boolean) {
    this.MIToMDA = MIToMDA;

    if (this.widgetUpdateValueCallback) {
      this.widgetUpdateValueCallback.apply(this, [{
        IsMI: this._MIToMDA
      }]);
    }
  }

  trackByIndex(index) {
    return index;
  }

  private getGroupedListItems(subjectData: any[], config: ISelectionResource): { id: string, key: string, subjects: any[]}[] {
    const groups = { };
    const sortConfig: ISortConfig[] = config.sortConfig ? config.sortConfig : DEFAULT_CLINICALRECOMMENDATION_GROUP_SORT_CONFIG;

    if (config.groupingIdentifier) {
      subjectData.forEach(subject => {
        const groupKey = jmespath.search(subject, config.groupingIdentifier.path);
        if (!groups[groupKey]) {
          groups[groupKey] = {
            subjects: [],
            key: groupKey,
            id: groupKey
          };
        }
        groups[groupKey].subjects.push(subject);
      });
    }

    function createPropertyCounterReducer(propertyPath, targetValue) {
      return (accumulator, currentValue) => {
        return accumulator + (get(currentValue, propertyPath) === targetValue ? 1 : 0);
      };
    }

    return Object.values(groups).sort((rec1: any, rec2: any) => {
      let localRes = 0;
      for (let i = 0; i < sortConfig.length; i++) {
        const sortCriterion = sortConfig[i];

        switch (sortCriterion.type) {
          case 'count':
            const rec1Count = rec1.subjects.reduce(createPropertyCounterReducer(sortCriterion.path, sortCriterion.value), 0);

            const rec2Count = rec2.subjects.reduce(createPropertyCounterReducer(sortCriterion.path, sortCriterion.value), 0);

            localRes = rec2Count - rec1Count;
            break;
          case 'condition':
            if (Array.isArray(sortCriterion.conditions)) {
              localRes = this.therapiesService.compareRecommendationsAgainstConditions(sortCriterion.conditions, rec1, rec2);
            }
            break;
          default:
            console.log('Sort Criterion type not found: ' + sortCriterion.type);
            break;
        }

        if (localRes != 0) {
          break;
        }
      }
      return localRes;
    }) as { id: string, key: string, subjects: any[]}[];
  }

  private molecularFinding(subjectsData: any[],config: ISelectionResource){
    let MF = [];
    subjectsData.forEach(subject => {
      if(subject?.patientData?.patientBiomarkers && subject?.patientData?.patientBiomarkers.length == 1 &&
        subject.patientData.patientBiomarkers[0].AberrationClass == "MolecularFinding" ){
        MF.push(subject);
      }
    })
    if (config.groupingIdentifier) {
      MF = this.getGroupedListItems(MF, config);
    }
    return MF;
  }

  private prepareListData(subjectsData: any[], config: ISelectionResource) {
    let filteredData = subjectsData.filter(subject => {
      if (Array.isArray(config.conditions)) {
        return config.conditions.every(condition => ConditionalItem.builder(subject, condition.value, condition.operator, condition.path).verify());
      } else {
        return true;
      }
    });

    if (config.groupingIdentifier) {
      filteredData = this.getGroupedListItems(filteredData, config);
    }

    // trials property added for the old genomics tests compatibility
    return filteredData.map(subject => ({
        ...subject,
        trials: this.therapiesAC.getTrials().pipe(
          map(trials => {
            if (config.subjectIdPath) {
              const selectedContextId = get(subject, config.subjectIdPath, '');
              const selectedContextType = config.subjectIdPath.split('.')[0].toLowerCase();

              return trials.filter(trial =>
                trial.context.find(context =>
                  context.resourceType.toLowerCase() === selectedContextType &&
                  context.reference === selectedContextId,
                )
              );
            }
            return trials;
          })
        )}
    ));
  }

  setInitSubjectId(subjectId: string, subjectIdPath: string, subjectSourceType: string) {
    if (!this._subjectId && this.autoSelectSubject) {
      this.setSubjectId(subjectId, subjectIdPath, subjectSourceType);
    }
  }

  setSubjectId(subjectId: string, subjectIdPath: string, subjectSourceType: string) {
    if (this.subjectId !== subjectId) {
      this.subjectId = subjectId;
      this.subjectIdPath = subjectIdPath;
      this.subjectSourceType = subjectSourceType;

      this.onSubjectIdentifierChange.emit({ subjectId, subjectIdPath, subjectSourceType});

      if (this.forceUpdate) {
        this.forceUpdate(true);
      }

      if (this.widgetUpdateValueCallback) {
        this.widgetUpdateValueCallback.apply(this, [{
          subjectId: this._subjectId,
          subjectIdPath: this._subjectIdPath,
          subjectSourceType: this._subjectSourceType
        }]);
      }

    }
  }

  isNewPanelConfigured(newPanelLayout) {
    return !isNil(newPanelLayout);
  }

  private resetShowInReportToggle() {
    const value = this.subjectsListConfiguration.showInReportToggleActive;
    if (value) {
      this.showInReportToggleActive = value;
    }
  }

  private resetShowInMIToggle() {
    const value = this.subjectsListConfiguration.showInMIToggleActive;
    if (value) {
      this.showInMIToggleActive = value;
    }
  }
}
